import React from 'react'
import PropTypes from 'prop-types'
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  Button,
  makeStyles
} from '@fluentui/react-components'
import { Warning24Filled } from '@fluentui/react-icons'

const useStyles = makeStyles({
  dialogSurface: {
    width: '90vw',
    maxWidth: '500px'
  },
  dialogBody: {
    display: 'flex',
    justifyContent: 'center'
  },
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: '16px'
  },
  warningIcon: {
    color: '#fce100',
    fontSize: '48px'
  },

  actionsContainer: {
    paddingTop: '20px',
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    width: '100%',
    flexWrap: 'wrap'
  }
})

/**
 * ConfirmationModal Component
 *
 * A reusable, centered dialog component used to prompt the user for confirmation
 * before executing a potentially destructive or significant action (e.g., deleting files, clearing chat).
 *
 * @param {Object} props - The component properties.
 * @param {boolean} props.isOpen - Controls the visibility of the modal.
 * @param {Function} props.onClose - Callback triggered when the modal is closed without confirming (e.g., clicking Cancel).
 * @param {Function} props.onConfirm - Callback triggered when the user clicks the primary confirmation button.
 * @param {boolean} props.isLoading - Indicates if the confirmed action is currently processing, disabling interactions.
 * @param {string} props.title - The header title text of the modal.
 * @param {string} props.message - The descriptive message or warning text displayed to the user.
 * @param {string} props.confirmButtonText - Custom text for the primary confirmation button (e.g., "Delete", "Clear").
 */
const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  title,
  message,
  confirmButtonText
}) => {
  const styles = useStyles()

  /**
  * Handles changes to the dialog's open state, such as when the user presses the 'Escape' key
  * or clicks on the backdrop overlay.
  * It acts as a guard to prevent the modal from closing if a background process (isLoading) is active.
  *
  * @param {Event} event - The UI event that triggered the state change.
  * @param {Object} data - An object containing the requested new state (data.open).
  */
  const handleOpenChange = (event, data) => {
    if (!data.open && !isLoading) {
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody className={styles.dialogBody}>
          <div className={styles.mainContainer}>
            <Warning24Filled
              className={styles.warningIcon}
              style={{ width: '48px', height: '48px' }}
            />

            {/* Title */}
            <DialogTitle>
              {title}
            </DialogTitle>

            {/* Message */}
            <p>
              {message}
            </p>

            {/* he buttons, now inside the main container */}
            <div className={styles.actionsContainer}>
              <Button
                appearance='secondary'
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                appearance='primary'
                onClick={onConfirm}
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : confirmButtonText}
              </Button>
            </div>
          </div>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}

ConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  confirmButtonText: PropTypes.string.isRequired
}

ConfirmationModal.defaultProps = {
  isLoading: false
}

export default ConfirmationModal
